/**
 * UserManagement Component
 * 
 * Handles user management functionality including:
 * - Displaying users in a table format
 * - Searching and filtering users
 * - Role management (edit/update user roles)
 * - User deletion
 * 
 * @component
 */
import React, { useState, useEffect } from 'react';
import { fetchAllUsers, updateUserRole, deleteUser } from '../../services/api';
import UserEditModal from './UserEditModal';

const UserManagement = () => {
    // State management for users
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editingUser, setEditingUser] = useState(null);
    const [newRole, setNewRole] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [filterRole, setFilterRole] = useState('all');
    const [editingUserData, setEditingUserData] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: 'registrationDate', direction: 'desc' });
    const LOCALE = 'en-GB';
    const DATE_FORMAT_OPTIONS = { timeZone: 'Etc/GMT' };

    // Available user roles
    const roles = ['user', 'admin', 'reviewer'];

    // Load users when component mounts
    useEffect(() => {
        loadUsers();
    }, []);

    /**
     * Fetches all users from the API
     */
    const loadUsers = async () => {
        try {
            setLoading(true);
            const data = await fetchAllUsers();
            setUsers(data);
        } catch (error) {
            console.error("Error fetching users:", error);
            setError("Failed to load users");
        } finally {
            setLoading(false);
        }
    };

    /**
     * Updates a user's role
     * @param {string} userId - The ID of the user to update
     * @param {string} role - The new role to assign
     */
    const handleRoleChange = async (userId, role) => {
        try {
            await updateUserRole(userId, role);
            setUsers(users.map(user => 
                user._id === userId ? { ...user, role } : user
            ));
            setEditingUser(null);
            setNewRole('');
            alert('User role updated successfully');
        } catch (error) {
            console.error("Error updating user role:", error);
            alert('Failed to update user role');
        }
    };

    /**
     * Deletes a user after confirmation
     * @param {string} userId - The ID of the user to delete
     */
    const handleDeleteUser = async (userId) => {
        if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
            try {
                await deleteUser(userId);
                setUsers(users.filter(user => user._id !== userId));
                alert('User deleted successfully');
            } catch (error) {
                console.error("Error deleting user:", error);
                alert('Failed to delete user');
            }
        }
    };

    /**
     * Opens the user edit modal
     * @param {Object} user - The user object to edit
     */
    const handleEditUser = (user) => {
        setEditingUserData(user);
        setShowEditModal(true);
    };

    /**
     * Closes the user edit modal
     */
    const handleCloseEditModal = () => {
        setShowEditModal(false);
        setEditingUserData(null);
    };

    /**
     * Handles user update after successful edit
     */
    const handleUserUpdated = () => {
        loadUsers(); // Reload all users to get the latest data
        handleCloseEditModal();
    };

    /**
     * Initiates role editing for a user
     * @param {Object} user - The user object to edit
     */
    const startEditRole = (user) => {
        setEditingUser(user._id);
        setNewRole(user.role);
    };

    /**
     * Cancels role editing
     */
    const cancelEdit = () => {
        setEditingUser(null);
        setNewRole('');
    };

    /**
     * Gets the appropriate badge color for a user role
     * @param {string} role - The user role
     * @returns {string} Bootstrap badge class
     */
    const getRoleBadgeColor = (role) => {
        switch (role) {
            case 'admin': return 'bg-danger';
            case 'reviewer': return 'bg-warning';
            case 'user': return 'bg-primary';
            default: return 'bg-secondary';
        }
    };

    /**
     * Filters users based on search term and role filter
     */
    const filteredUsers = users.filter(user => {
        const matchesSearch = 
            user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.username?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesRole = filterRole === 'all' || user.role === filterRole;
        
        return matchesSearch && matchesRole;
    });

    const getSortValue = (user, key) => {
        switch (key) {
            case 'user':
                return `${user.lastName || ''} ${user.firstName || ''} ${user.username || ''}`.trim();
            case 'contact':
                return user.email || '';
            case 'institution':
                return user.primaryAffiliation?.institution || '';
            case 'role':
                return user.role || '';
            case 'registrationDate':
                return user.registrationDate ? new Date(user.registrationDate).getTime() : 0;
            default:
                return '';
        }
    };

    const sortedUsers = [...filteredUsers].sort((a, b) => {
        const aVal = getSortValue(a, sortConfig.key);
        const bVal = getSortValue(b, sortConfig.key);

        let comparison = 0;
        if (typeof aVal === 'number' && typeof bVal === 'number') {
            comparison = aVal - bVal;
        } else {
            comparison = aVal.toString().toLowerCase().localeCompare(bVal.toString().toLowerCase());
        }

        return sortConfig.direction === 'asc' ? comparison : -comparison;
    });

    const handleSort = (key) => {
        setSortConfig(prev => {
            if (prev.key === key) {
                return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { key, direction: 'asc' };
        });
    };

    const renderSortIcon = (key) => {
        if (sortConfig.key !== key) return <i className="bi bi-arrow-down-up ms-1 text-muted"></i>;
        return sortConfig.direction === 'asc' 
            ? <i className="bi bi-sort-alpha-down ms-1"></i>
            : <i className="bi bi-sort-alpha-up ms-1"></i>;
    };

    return (
        <div className="card">
            <div className="card-header">
                <div className="d-flex justify-content-between align-items-center">
                    <h3 className="mb-0">
                        <i className="bi bi-people me-2"></i>
                        User Management
                    </h3>
                    <span className="badge bg-info">{filteredUsers.length} users</span>
                </div>
            </div>
            <div className="card-body">
                {/* Search and Filter Controls */}
                <div className="row mb-3">
                    <div className="col-md-6">
                        <div className="input-group">
                            <span className="input-group-text">
                                <i className="bi bi-search"></i>
                            </span>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search users..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="col-md-3">
                        <select
                            className="form-select"
                            value={filterRole}
                            onChange={(e) => setFilterRole(e.target.value)}
                        >
                            <option value="all">All Roles</option>
                            <option value="user">Users</option>
                            <option value="reviewer">Reviewers</option>
                            <option value="admin">Admins</option>
                        </select>
                    </div>
                    <div className="col-md-3">
                        <button
                            className="btn btn-outline-secondary w-100"
                            onClick={loadUsers}
                        >
                            <i className="bi bi-arrow-clockwise me-1"></i>
                            Refresh
                        </button>
                    </div>
                </div>

                {/* Users Table */}
                {loading ? (
                    <div className="text-center py-4">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : error ? (
                    <div className="alert alert-danger" role="alert">
                        {error}
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead className="table-light">
                                <tr>
                                    <th role="button" onClick={() => handleSort('user')} className="align-middle" style={{cursor:'pointer'}}>
                                        <div className="d-flex align-items-center">
                                            User Info
                                            {renderSortIcon('user')}
                                        </div>
                                    </th>
                                    <th role="button" onClick={() => handleSort('contact')} className="align-middle" style={{cursor:'pointer'}}>
                                        <div className="d-flex align-items-center">
                                            Contact
                                            {renderSortIcon('contact')}
                                        </div>
                                    </th>
                                    <th role="button" onClick={() => handleSort('institution')} className="align-middle" style={{cursor:'pointer'}}>
                                        <div className="d-flex align-items-center">
                                            Institution
                                            {renderSortIcon('institution')}
                                        </div>
                                    </th>
                                    <th role="button" onClick={() => handleSort('role')} className="align-middle" style={{cursor:'pointer'}}>
                                        <div className="d-flex align-items-center">
                                            Role
                                            {renderSortIcon('role')}
                                        </div>
                                    </th>
                                    <th role="button" onClick={() => handleSort('registrationDate')} className="align-middle" style={{cursor:'pointer'}}>
                                        <div className="d-flex align-items-center">
                                            Registration Date
                                            {renderSortIcon('registrationDate')}
                                        </div>
                                    </th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedUsers.map((user) => (
                                    <tr key={user._id}>
                                        <td>
                                            <div>
                                                <strong>
                                                    {user.salutation} {user.firstName} {user.lastName}
                                                </strong>
                                                <br />
                                                <small className="text-muted">
                                                    <i class="bi bi-person-circle"></i>
                                                    {' '}
                                                    {user.username}
                                                </small>
                                                {user.isEmailConfirmed && (
                                                    <i className="bi bi-check-circle-fill text-success ms-1" title="Email confirmed"></i>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <div>
                                                <small>{user.email}</small>
                                                {user.workPhone && (
                                                    <>
                                                        <br />
                                                        <small className="text-muted">
                                                            <i className="bi bi-telephone me-1"></i>
                                                            {user.workPhoneCountry} {user.workPhone}
                                                        </small>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <div>
                                                {user.primaryAffiliation?.institution && (
                                                    <>
                                                        <small>{user.primaryAffiliation.institution}</small>
                                                        {user.primaryAffiliation?.department && (
                                                            <>
                                                                <br />
                                                                <small className="text-muted">
                                                                    {user.primaryAffiliation.department}
                                                                </small>
                                                            </>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            {editingUser === user._id ? (
                                                <div className="d-flex align-items-center gap-2">
                                                    <select
                                                        className="form-select form-select-sm"
                                                        value={newRole}
                                                        onChange={(e) => setNewRole(e.target.value)}
                                                        style={{ width: '120px' }}
                                                    >
                                                        {roles.map(role => (
                                                            <option key={role} value={role}>
                                                                {role.charAt(0).toUpperCase() + role.slice(1)}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <button
                                                        className="btn btn-success btn-sm"
                                                        onClick={() => handleRoleChange(user._id, newRole)}
                                                        title="Save"
                                                    >
                                                        <i className="bi bi-check"></i>
                                                    </button>
                                                    <button
                                                        className="btn btn-secondary btn-sm"
                                                        onClick={cancelEdit}
                                                        title="Cancel"
                                                    >
                                                        <i className="bi bi-x"></i>
                                                    </button>
                                                </div>
                                            ) : (
                                                <span 
                                                    className={`badge ${getRoleBadgeColor(user.role)}`}
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => startEditRole(user)}
                                                    title="Click to edit role"
                                                >
                                                    {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            <small>
                                                {new Date(user.registrationDate).toLocaleDateString(LOCALE, DATE_FORMAT_OPTIONS)}
                                                {user.lastLogin && (
                                                    <>
                                                        <br />
                                                        <span className="text-muted">
                                                            Last: {new Date(user.lastLogin).toLocaleDateString(LOCALE, DATE_FORMAT_OPTIONS)}
                                                        </span>
                                                    </>
                                                )}
                                            </small>
                                        </td>
                                        <td>
                                            <div className="d-flex gap-1">
                                                <button
                                                    className="btn btn-outline-primary btn-sm"
                                                    onClick={() => handleEditUser(user)}
                                                    title="Edit User"
                                                >
                                                    <i className="bi bi-pencil"></i>
                                                </button>
                                                <button
                                                    className="btn btn-outline-danger btn-sm"
                                                    onClick={() => handleDeleteUser(user._id)}
                                                    title="Delete User"
                                                >
                                                    <i className="bi bi-trash"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {filteredUsers.length === 0 && (
                            <div className="text-center py-4">
                                <i className="bi bi-person-x" style={{ fontSize: '3rem', color: '#6c757d' }}></i>
                                <p className="text-muted mt-2">
                                    {searchTerm || filterRole !== 'all' ? 'No users match your search criteria.' : 'No users found.'}
                                </p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* User Edit Modal */}
            <UserEditModal
                isOpen={showEditModal}
                onClose={handleCloseEditModal}
                user={editingUserData}
                onUserUpdated={handleUserUpdated}
            />
        </div>
    );
};

export default UserManagement;
